import { NavLink, Outlet } from 'react-router-dom';
import { GuestGate } from '../../components/bidder-web';
import { useSession } from '../../hooks/useSession';

/**
 * The account — ONE page with a rail, not five separate ones.
 *
 * REBUILT to the web design handoff (`web/web-account.jsx`, 2026-09-02). The
 * bidder's account was five unconnected pages reached from a header link:
 * dashboard, invoices, payment methods, profile, and nothing at all for pickup.
 * Each was a dead end — paying an invoice told you nothing about the other one
 * still owed, or about the yard closing on Saturday.
 *
 * 🔴 THE RAIL STAYS VISIBLE, AND THAT IS THE WHOLE POINT. A bidder settling one
 * invoice can see what else is outstanding and when collection ends, which is
 * the question they ask next about ninety percent of the time. On the phone
 * that was a stack of pushed screens because there was no room; the web has the
 * room and it should spend it here.
 *
 * 🔴 THE SECTIONS ARE STILL SEPARATE ROUTES. `/my/invoices/:id` has to be
 * linkable — from an email, from a notification, from the sale list — and a
 * tabbed single route would break every one of those links. The rail is
 * chrome around real routes, not a state machine pretending to be one.
 *
 * GUESTS GET A GATE, NOT AN EMPTY ACCOUNT. Row-level security returns nothing
 * to a signed-out visitor, so every section would render blank — reading as
 * "you have nothing", which is a claim we cannot make about somebody we have
 * not identified.
 */

const SECTIONS: { to: string; label: string; end?: boolean }[] = [
  { to: '/my/dashboard', label: 'Overview', end: true },
  { to: '/my/bidding', label: 'Your bidding' },
  { to: '/my/invoices', label: 'Invoices' },
  { to: '/my/payment-methods', label: 'Payment methods' },
  { to: '/my/pickup', label: 'Pickup' },
  { to: '/my/profile', label: 'Settings' },
];

export function AccountLayout() {
  const { session, loading } = useSession();

  if (loading) {
    return (
      <div className="w-wrap" style={{ padding: '48px 0' }}>
        <p className="muted">Loading your account…</p>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="w-wrap" style={{ padding: '48px 0' }}>
        <GuestGate
          title="Your account is behind a sign-in"
          body="Your bids, invoices, cards and collection details are only shown to you. Sign in and they are all here."
        />
      </div>
    );
  }

  return (
    <div className="w-wrap">
      <div className="wa">
        <nav className="wa-rail" aria-label="Your account">
          {SECTIONS.map((s) => (
            <NavLink key={s.to} to={s.to} end={s.end ?? false} className="wa-item">
              {s.label}
            </NavLink>
          ))}
        </nav>
        <div style={{ minWidth: 0 }}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}
